import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  ListItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountData, PassOrderPageData } from '../../api';
import { getActiveLocations } from '../../common/filter/locationGrouping';
import { getPassOrderPages } from '../../utils/AccountUtils';
import { useLanguage } from '../../common/GeneralUtils';
import { buildPassTemplateLocationMap } from './derivePassTemplateLocation';

type Props = {
  account: AccountData;
  selectedIds: string[] | null;
  onChange: (next: string[] | null) => void;
};

type Group = {
  key: string;
  locationName: string | null; // null → "undetermined"
  templates: PassOrderPageData[];
};

export const SubscriptionsFilterSection = ({ account, selectedIds, onChange }: Props) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const language = useLanguage();

  const activeTemplates = getPassOrderPages(account);
  const allIds = activeTemplates.map((tpl) => tpl.id);
  const activeLocations = getActiveLocations(account);
  const multiLocation = activeLocations.length > 1;

  const currentIds = selectedIds ?? allIds;

  const setCurrentIds = (next: string[]) => {
    onChange(next.length === allIds.length ? null : next);
  };

  const toggleTemplate = (id: string, checked: boolean) => {
    const next = checked ? [...currentIds, id] : currentIds.filter((x) => x !== id);
    setCurrentIds(next);
  };

  const allSelected = allIds.length > 0 && allIds.every((id) => currentIds.includes(id));
  const toggleAll = () => setCurrentIds(allSelected ? [] : [...allIds]);

  const groups: Group[] = (() => {
    if (!multiLocation) {
      return [{ key: '__all__', locationName: null, templates: activeTemplates }];
    }
    const locationMap = buildPassTemplateLocationMap(account);
    const bucket = new Map<string, Group>();
    for (const tpl of activeTemplates) {
      const locId = locationMap.get(tpl.id) ?? null;
      const key = locId ?? '__undetermined__';
      if (!bucket.has(key)) {
        const loc = locId ? activeLocations.find((l) => l.id === locId) : undefined;
        const name = loc ? loc.name?.[language] ?? loc.name?.en ?? '' : null;
        bucket.set(key, { key, locationName: name, templates: [] });
      }
      bucket.get(key)!.templates.push(tpl);
    }
    // Named locations first (alphabetical), "undetermined" last.
    return Array.from(bucket.values()).sort((a, b) => {
      if (a.locationName === null) return 1;
      if (b.locationName === null) return -1;
      return a.locationName.localeCompare(b.locationName);
    });
  })();

  if (activeTemplates.length === 0) return null;

  return (
    <>
      <ListItem sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Typography variant="overline" color="text.secondary">
            {t('passes.filter.subscription')}
          </Typography>
          {allIds.length > 1 && (
            <Button
              size="small"
              onClick={toggleAll}
              sx={{ textTransform: 'none', py: 0, minWidth: 0, color: 'text.secondary' }}>
              <Typography variant="caption">
                {allSelected ? t('calendar.filter.deselect_all') : t('calendar.filter.select_all')}
              </Typography>
            </Button>
          )}
        </Box>
      </ListItem>
      <Grid container mt={-1}>
        {groups.map((group) => {
          const showHeader = multiLocation;
          const headerText =
            group.locationName === null
              ? multiLocation
                ? t('passes.filter.location_undetermined')
                : ''
              : group.locationName;
          return (
            <React.Fragment key={group.key}>
              {showHeader && headerText && (
                <ListItem sx={{ py: 0 }}>
                  <Typography variant="caption" color="text.disabled">
                    {headerText}
                  </Typography>
                </ListItem>
              )}
              {group.templates.map((tpl) => (
                <ListItem key={tpl.id} disablePadding sx={{ paddingInlineStart: showHeader ? 3 : 1 }}>
                  <FormControlLabel
                    sx={{ width: '100%', mx: 0, py: isDesktop ? 0 : 0.5 }}
                    label={tpl.label?.[language] ?? tpl.label?.en ?? ''}
                    control={
                      <Checkbox
                        size={isDesktop ? 'small' : 'medium'}
                        checked={currentIds.includes(tpl.id)}
                        onChange={(e) => toggleTemplate(tpl.id, e.target.checked)}
                      />
                    }
                  />
                </ListItem>
              ))}
            </React.Fragment>
          );
        })}
      </Grid>
      <Divider />
    </>
  );
};
